import axios from "axios";

/**
 * Training ids for a catalogue (excludes archived rows only; includes active + inactive).
 */
export async function fetchTrainingIdsForCatalogue(supabase, catalogueId) {
  if (!catalogueId) return [];
  const { data, error } = await supabase
    .from("training_catalogue_data")
    .select("id")
    .eq("catalogue_id", catalogueId)
    .neq("status", "archived");

  if (error) throw error;
  return (data || []).map((r) => r.id).filter(Boolean);
}

/**
 * Returns distinct training_ids that appear in selected_trainings (allocated in plans).
 */
export async function getAllocatedTrainingIds(supabase, trainingIds) {
  if (!trainingIds?.length) return [];
  const { data, error } = await supabase
    .from("selected_trainings")
    .select("training_id")
    .in("training_id", trainingIds);

  if (error) {
    console.error("getAllocatedTrainingIds:", error);
    throw error;
  }
  return [...new Set((data || []).map((r) => r.training_id).filter(Boolean))];
}

/**
 * Remove plan/training-need links for a training before archiving/deleting it.
 * `allocate_training_program` rows are removed automatically via DB ON DELETE CASCADE
 * from `selected_trainings.id` -> `allocate_training_program.selected_training_id`.
 */
export async function deleteTrainingSelectionsByTrainingId(
  supabase,
  trainingId
) {
  if (!trainingId) return { deletedSelectedTrainings: 0 };

  const { data, error: deleteSelectedTrainingsError } = await supabase
    .from("selected_trainings")
    .delete()
    .eq("training_id", trainingId)
    .select("id");

  if (deleteSelectedTrainingsError) {
    throw deleteSelectedTrainingsError;
  }

  return { deletedSelectedTrainings: data?.length || 0 };
}

async function putVectorTrainingUpdate(trainingId, updates) {
  const response = await axios.put(
    `${process.env.REACT_APP_BACKEND_BASE_URL}/importTrainingCatalogue`,
    { trainingId, updates },
    {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${process.env.REACT_APP_BACKEND_API_KEY}`,
      },
    }
  );
  if (!response.data?.success) {
    return {
      success: false,
      error: response.data?.message || response.data?.error,
    };
  }
  return { success: true };
}

/**
 * Sync a single training's `status` in Qdrant (payload) to match training_catalogue_data.
 */
export async function syncTrainingStatusToVectorDB(trainingId, status) {
  try {
    return await putVectorTrainingUpdate(trainingId, { status });
  } catch (e) {
    console.warn("syncTrainingStatusToVectorDB:", e.message || e);
    return { success: false, error: e.message || "Vector update failed" };
  }
}

/**
 * Batch sync many trainings (non-throwing per id; logs failures).
 */
export async function batchSyncTrainingStatusToVectorDB(trainingIds, status) {
  if (!trainingIds?.length) return;
  const results = await Promise.allSettled(
    trainingIds.map((id) => syncTrainingStatusToVectorDB(id, status))
  );
  const failed = results.filter(
    (r) =>
      r.status === "rejected" ||
      (r.status === "fulfilled" && r.value && !r.value.success)
  );
  if (failed.length) {
    console.warn(
      `Vector DB: ${failed.length}/${trainingIds.length} updates failed for status=${status}`
    );
  }
}
